import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import SurveysSelector from '~src/modules/heatmap/components/header/SurveysSelector';
import { AnalysisSpiderParams } from '~src/modules/spidergraph/types';
import { FilterTypes } from '~src/modules/surveys/constants/filterOptions';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import {
  FilterCol,
  FilterDescription,
  FilterTitle,
  FilterTitleAndDescription,
  FiltersContainer,
  FiltersRow,
  SelectorBox,
  SelectorStep,
} from './spiderGraphFilterDetails.sc';

interface Props {
  urlParams?: AnalysisSpiderParams;
  activeSelect?: SurveySubjectTypes;
  activeSurveyId?: string;
  onSurveyChange(selectedItem: OptionItem, selectedType: SurveySubjectTypes): void;
  hidePeopleSelector?: boolean;
  isLoading?: boolean;
  shouldHighlight?: boolean;
}

const SpiderGraphSelector: FC<Props> = (props) => {
  const [t] = useTranslation();

  const { isLoading } = props;

  const onPulseSelect = (opt: OptionItem) => {
    props.onSurveyChange(opt, SurveySubjectTypes.General);
  };

  const onGroupSelect = (opt: OptionItem) => {
    props.onSurveyChange(opt, SurveySubjectTypes.Groups);
  };

  const onPeopleSelect = (opt: OptionItem) => {
    props.onSurveyChange(opt, SurveySubjectTypes.People);
  };

  return (
    <FiltersContainer
      $disabled={Boolean(isLoading)}
      className={props.shouldHighlight ? 'highlighted' : ''}
    >
      {!props.hidePeopleSelector && <FilterCol>
        <SelectorStep>1</SelectorStep>
      </FilterCol>
      }
      <FilterCol>
        <FilterTitleAndDescription>
          <FilterTitle data-translation="x.spider_graph.header.filters.select_survey.title">
            {t('x.spider_graph.header.filters.select_survey.title')}
          </FilterTitle>
          <FilterDescription data-translation="x.spider_graph.header.filters.select_survey.description">
            {t('x.spider_graph.header.filters.select_survey.description')}
          </FilterDescription>
        </FilterTitleAndDescription>
        <FiltersRow>
          <FilterCol>
            <SelectorBox $inactive={props.activeSelect !== SurveySubjectTypes.General} $isFirstChild>
              <SurveysSelector
                triggerIconName="iconPulse"
                surveyType={SurveySubjectTypes.General}
                filterSubject={FilterTypes.General}
                initialOpt={
                  (props.activeSelect === SurveySubjectTypes.General && props.activeSurveyId) || null
                }
                inactive={props.activeSelect !== SurveySubjectTypes.General}
                onSelect={onPulseSelect}
              />
            </SelectorBox>
          </FilterCol>
          <SelectorBox $inactive={props.activeSelect !== SurveySubjectTypes.Groups}>
            <SurveysSelector
              triggerIconName="iconGroups"
              surveyType={SurveySubjectTypes.Groups}
              filterSubject={FilterTypes.Groups}
              initialOpt={(props.activeSelect === SurveySubjectTypes.Groups && props.activeSurveyId) || null}
              inactive={props.activeSelect !== SurveySubjectTypes.Groups}
              onSelect={onGroupSelect}
            />
          </SelectorBox>
          <SelectorBox
            $inactive={props.activeSelect !== SurveySubjectTypes.People}
            $show={Boolean(props.hidePeopleSelector)}
          >
            <SurveysSelector
              triggerIconName="iconPeople"
              surveyType={SurveySubjectTypes.People}
              filterSubject={FilterTypes.People}
              initialOpt={(props.activeSelect === SurveySubjectTypes.People && props.activeSurveyId) || null}
              inactive={props.activeSelect !== SurveySubjectTypes.People}
              onSelect={onPeopleSelect}
            />
          </SelectorBox>
        </FiltersRow>
      </FilterCol>
    </FiltersContainer>
  );
};

export default SpiderGraphSelector;
